<template>
  <div
    v-loading="initiallLoading"
    v-infinite-scroll="loadMore"
    class="call-fb-cards-container"
    :infinite-scroll-disabled="infiniteScrollDisabled"
    infinite-scroll-distance="20"
  >
    <div class="header">
      <div class="header-left">
        {{ i18n.t('general.numberOf', { number: paginationMeta?.totalCount || 0 }) }}
      </div>
      <div class="header-right">
        <div class="legend">
          <div class="left-label">
            {{ i18n.t('training.callFb.rating.fail') }}
          </div>
          <div class="color-bar">
            <div class="rating-fail" />
            <div class="rating-passable" />
            <div class="rating-good" />
          </div>
          <div class="right-label">
            {{ i18n.t('training.callFb.rating.good') }}
          </div>
        </div>
      </div>
    </div>
    <div class="content">
      <div
        v-if="!callFeedbacks.length"
        class="not-exists"
      >
        <BEmptyBox
          display-only
        >
          {{ i18n.t('apiError.notFound') }}
        </BEmptyBox>
      </div>
      <template v-else>
        <CallFbCard
          v-for="callFeedback in callFeedbacks"
          :key="callFeedback.callResultId"
          :call-feedback="callFeedback"
          @open-ai-feedback-modal="handleOpenAiFeedbackModal"
        />
        <BLayout
          v-if="infiniteLoading"
          class="py-200"
          justify-center
        >
          <BElementIcon name="Loading" />
        </BLayout>
      </template>
    </div>
    <AiFeedbackModal
      :modal-name="AI_FEEDBACK_MODAL_NAME"
      :call-feedback="selectedCallFeedback"
      @close="handleCloseAiFeedbackModal"
      @refetch="handleRefetchAiFeedbackModal"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, watch, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { CallFeedback } from '@/api/openapi';
import { CallFeedbackApiService } from '@/api/user/resources/call_feedback';
import { useInfiniteLoading } from '@/composable/infinite-loading';
import { useAlert } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_MODAL } from '@/injection-keys';
import AiFeedbackModal from './AiFeedbackModal.vue';
import CallFbCard from './CallFbCard.vue';

type TProps = {
  callerId: number | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  defaultCallResultTypeId: number | null;
  customCallResultTypeId: number | null;
  callConnectionPoint: string | null;
  callResultId: number | null;
};

const props = defineProps<TProps>();

const { wait } = useWait();
const { alert } = useAlert();

const selectedCallFeedback = ref<CallFeedback>();
const AI_FEEDBACK_MODAL_NAME = 'aiFeedbackModal';

const initiallLoading = computed(() => {
  return callFeedbacks.value.length === 0 && wait.is(['fetchCallFeedbacksWait']);
});
const infiniteLoading = computed(() => {
  return wait.is(['fetchCallFeedbacksWait']);
});

const i18n = useI18n();
const modal = inject(KEY_OF_MODAL);

const fetchCallFeedbacks = async (page: number) => {
  if (props.startDate == null || props.endDate == null) return null;
  const api = new CallFeedbackApiService();
  const response = await api.getCallFeedbacks({
    request: {
      page,
      startDate: props.startDate,
      endDate: props.endDate,
      userId: props.callerId ?? undefined,
      connectedTo: props.callConnectionPoint ?? undefined,
      callResultTypeId: props.customCallResultTypeId ?? undefined,
      defaultCallResultTypeId: props.defaultCallResultTypeId ?? undefined,
      callResultId: props.callResultId ?? undefined,
    },
    errorHandlers: {
      400: (error) => {
        alert({
          title: i18n.t('general.error'),
          message: error.response.data.errorMessage,
        });
      },
    },
  });
  return {
    items: response.data.callFeedbacks,
    paginationMeta: response.data.meta,
  };
};

const { refetch, loadMore, items: callFeedbacks, paginationMeta, infiniteScrollDisabled } = useInfiniteLoading<CallFeedback>(fetchCallFeedbacks, 'fetchCallFeedbacksWait', false);

watch([
  () => props.callerId,
  () => props.startDate,
  () => props.endDate,
  () => props.defaultCallResultTypeId,
  () => props.customCallResultTypeId,
  () => props.callConnectionPoint,
  () => props.callResultId,
], async () => {
  await nextTick();
  refetch();
});

const callResultIdParams = ref<number>();

onMounted(async () => {
  callResultIdParams.value = props.callResultId;
});

watch(callFeedbacks, async () => {
  if (callFeedbacks.value.length > 0 && callResultIdParams.value) {
    // 初回のみモーダルを開く、画面内の条件変更時にモーダルが自動的に開くのを防ぐ
    callResultIdParams.value = null;
    const callFeedback = callFeedbacks.value.find((e) => e.callResultId === props.callResultId);
    handleOpenAiFeedbackModal(callFeedback);
  }
});

const handleOpenAiFeedbackModal = (value: CallFeedback) => {
  selectedCallFeedback.value = value;
  modal.show(AI_FEEDBACK_MODAL_NAME);
};

const handleCloseAiFeedbackModal = () => {
  modal.hide(AI_FEEDBACK_MODAL_NAME);
};

const handleRefetchAiFeedbackModal = () => {
  modal.hide(AI_FEEDBACK_MODAL_NAME);
  refetch();
};
</script>

<style lang="scss" scoped>
.call-fb-cards-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-y: auto;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: $basespace-300;
  border-bottom: 1px solid $bdcolor-base;
  min-width: 972px;
}

.header-left {
  font-size: 16px;
  font-weight: 700;
}

.legend {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: $basespace-50;

  font-size: $fontsize-100;
  font-weight: 400;
  color: $textcolor-light;
  line-height: 100%;
}

.color-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 0;

  div {
    height: 4px;
    width: 32px;
  }

  div:first-child {
    border-radius: 100px 0 0 100px;
  }
  div:last-child {
    border-radius: 0 100px 100px 0;
  }
}

.rating-fail {
  background-color: $basecolor-error;
}
.rating-passable {
  background-color: $basecolor-secondary;
}
.rating-good {
  background-color: $basecolor-success;
}

.content {
  min-width: 972px;
}

.not-exists {
  margin-top: $basespace-300;
}
</style>
